<template>
  <div class="detalle-empresa-agente">
    <DxTabPanel ref="tabPanel" height="95%" tabs-position="top" icon-position="left" :defer-rendering="false">
      <DxItem :key="1" :title="tituloTab1" class="tab-item" icon="fa-regular fa-file-lines">
        <empresa-agentes-tab-datos :model="localModel" ref="tabDatos"></empresa-agentes-tab-datos>
      </DxItem>
      <DxItem :key="2" :title="tituloTab2" class="tab-item" icon="fa-solid fa-user-tie">
        <empresa-agentes-tab-representante :model="localModel" ref="tabRepresentante"></empresa-agentes-tab-representante>
      </DxItem>
      <DxItem :key="3" :title="tituloTab3" class="tab-item" icon="fa-regular fa-folder-open">
        <empresa-agentes-documentacion-panel :id-empresa-agentes="localModel.Id" :estructura-documental="localModel.EstructuraDocumental ?? []"
          ref="tabDocumentacion" @documentacion-changed="syncValidarBoton" @documento-subido="onDocumentacionSubida"
          documentos-opcionales></empresa-agentes-documentacion-panel>
      </DxItem>
      <DxItem v-if="mostrarTabRechazo" :key="4" :title="tituloTabRechazo" class="tab-item" icon="fa-solid fa-ban">
        <empresa-agentes-tab-rechazo :model="localModel"></empresa-agentes-tab-rechazo>
      </DxItem>
    </DxTabPanel>
  </div>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, nextTick, onMounted, ref, watch } from 'vue';
import DxTabPanel, { DxItem } from 'devextreme-vue/tab-panel';
import { useIntl } from '@/.shared/services/i18n/i18nservice';
import { useModalBuilder, type ToolbarReference } from '@/.shared/helpers/modal-building-helper';
import type { DxButton } from 'devextreme-vue';
import { messageBoxResult } from '@/.shared/components/dialogs/message-box';
import { cancelButtonProps, dangerButtonProps, primaryButtonProps } from '@/.shared/helpers/button-helper';
import EmpresaAgentesTabRepresentante from '@/components/empresas-agentes/EmpresaAgentesTabRepresentante.vue';
import EmpresaAgentesTabDatos from '@/components/empresas-agentes/EmpresaAgentesTabDatos.vue';
import EmpresaAgentesDocumentacionPanel from '@components/empresa-agentes/EmpresaAgentesDocumentacionPanel.vue';
import EmpresaAgentesTabRechazo from '@/components/empresas-agentes/EmpresaAgentesTabRechazo.vue';
import { EstadoEmpresaAgentesEnum, type DetailEmpresaAgentesModel } from '@/.shared/.api';
import { useApi } from '@/.shared/.api/integration';
import { toastError, toastSuccess } from '@/.shared/services/notification-service';
import { useModalService } from '@/.shared/services/modal-service';

const intl = useIntl();
const api = useApi();
const tabDatos = ref<InstanceType<typeof EmpresaAgentesTabDatos>>();
const tabDocumentacion = ref<InstanceType<typeof EmpresaAgentesDocumentacionPanel>>();
const tabRepresentante = ref<InstanceType<typeof EmpresaAgentesTabRepresentante>>();
const tabPanel = ref<InstanceType<typeof DxTabPanel>>();

const validarToolbarButton = ref<ToolbarReference<DxButton> | null>(null);

const props = defineProps({
  model: {
    type: Object as () => DetailEmpresaAgentesModel,
    required: true
  },
});

const localModel = ref<DetailEmpresaAgentesModel>(props.model);

watch(
  () => props.model,
  (m) => {
    localModel.value = m;
  },
);

const tituloTab1 = computed(() => `${intl.i18n.t('empresa_agente.form.datos')}`);
const tituloTab2 = computed(() => `${intl.i18n.t('empresa_agente.form.representante')}`);
const tituloTab3 = computed(() => `${intl.i18n.t('empresa_agente.form.documentos')}`);
const tituloTabRechazo = computed(() => intl.i18n.t("empresa_agente.form.rechazo"));

const mostrarTabRechazo = computed(() => {
  const f = localModel.value.FechaRechazo;
  if (f == null) {
    return false;
  }
  
  return true;
});

watch(
  () => localModel.value.EstructuraDocumental,
  () => {
    void nextTick(() => {
      syncValidarBoton();
    });
  },
  { deep: true },
);

onMounted(async () => {
  const modal = useModalBuilder();
  modal.setContext(getCurrentInstance()!);

  configureButtons(modal);
  await nextTick();
  syncValidarBoton();
});

function syncValidarBoton(): void {
  const btn = validarToolbarButton.value;
  if (!btn) {
    return;
  }
  const docOk = tabDocumentacion.value?.isDocumentacionValid() ?? false;
  btn.update((w) => {
    w.disabled = !docOk;
  });
}

function configureButtons(modal: ReturnType<typeof useModalBuilder>) {
  let cancelarButton: ToolbarReference<DxButton>;
  let validarButton: ToolbarReference<DxButton>;
  let rechazarButton: ToolbarReference<DxButton>;
  let cancelarFirmaButton: ToolbarReference<DxButton>;

  cancelarButton = modal.addButton(
    "cancelar",
    cancelButtonProps(),
    { toolbar: "bottom", location: "after" },
  );
  cancelarButton.update((w) => {
    w.onClick = () => modal.close(messageBoxResult.cancel);
  });

  if (localModel.value.IdEstado === EstadoEmpresaAgentesEnum.PdtValidar) {
    validarButton = modal.addButton(
      "validar",
      primaryButtonProps(intl.i18n.t("empresas_agentes.pendientes_validacion.validar")),
      { toolbar: "bottom", location: "after" },
    );
    validarToolbarButton.value = validarButton;
    validarButton.update((w) => {
      w.disabled = true;
      w.onClick = () => {
        void validarYEnviarAFirma().then((ok) => {
          if (ok) {
            modal.close(messageBoxResult.ok);
          }
        });
      };
    });

    rechazarButton = modal.addButton(
      "rechazar",
      dangerButtonProps(intl.i18n.t("empresas_agentes.pendientes_validacion.rechazar")),
      { toolbar: "bottom", location: "after" },
    );
    rechazarButton.update((w) => {
      w.onClick = () => {
        void rechazar().then((ok) => {
          if (ok) {
            modal.close(messageBoxResult.no);
          }
        });
      };
    });
  }

  if (localModel.value.IdEstado === EstadoEmpresaAgentesEnum.PdtFirma) {
    cancelarFirmaButton = modal.addButton(
      "cancelarFirma",
      dangerButtonProps(intl.i18n.t("empresas_agentes.pendientes_validacion.cancelar_firma")),
      { toolbar: "bottom", location: "after" },
    );
    cancelarFirmaButton.update((w) => {
      w.onClick = () => {
        void cancelarFirma().then((ok) => {
          if (ok) {
            modal.close(messageBoxResult.no);
          }
        });
      };
    });
  }
}

const onDocumentacionSubida = async (): Promise<void> => {
  try {
    const data = await api.callStandardApi("getEmpresaAgentesDetail", { showLoading: true }, localModel.value.Id);
    localModel.value = data;
  } catch {
    /* el tab ya mostró error de subida; el modelo local puede quedar desfasado */
  }
  await nextTick();
  syncValidarBoton();
};

const validarYEnviarAFirma = async (): Promise<boolean> => {
  if (!tabDocumentacion.value?.isDocumentacionValid()) {
    return false;
  }

  try {
    await api.callStandardApi("sendToSignEmpresaAgentes", { showLoading: true }, localModel.value.Id);
    toastSuccess(intl.i18n.t("empresa_agentes.validacion.exito"));
    return true;
  } catch {
    toastError(intl.i18n.t("empresa_agentes.validacion.error"));
    return false;
  }
};

const rechazar = async (): Promise<boolean> => {
  try {
    const modalService = useModalService();
    const motivo = await modalService.inputBox(intl.i18n.t("empresa_agentes.rechazo.comentario"), "dxTextArea", null, true);

    if (motivo === undefined) {
      return false;
    }

    await api.callStandardApi("rejectEmpresaAgentes", { showLoading: true }, localModel.value.Id, motivo!);
    toastSuccess(intl.i18n.t("empresa_agentes.rechazo.exito"));
    return true;
  } catch {
    toastError(intl.i18n.t("empresa_agentes.rechazo.error"));
    return false;
  }
};

const cancelarFirma = async (): Promise<boolean> => {
  try {
    await api.callStandardApi("cancelSignEmpresaAgentes", { showLoading: true }, localModel.value.Id);
    toastSuccess(intl.i18n.t("empresa_agentes.cancelacion_firma.exito"));
    return true;
  } catch {
    toastError(intl.i18n.t("empresa_agentes.cancelacion_firma.error"));
    return false;
  }
};

</script>